/**
 * Signing in with a Frappe username and password — the `frappe` auth provider (Akhil,
 * 2026-09-24), for an instance served by the Frappe site itself with Clerk switched off.
 *
 * There is no exchange and no token: `POST /api/method/login` sets the site's own `sid` cookie
 * and every later call carries it, exactly as Desk does. Nothing is kept here — no token in
 * memory, nothing in browser storage (barred) — so a reload simply asks the server who it is.
 *
 * Measured against rfqdev 2026-09-24: a wrong password answers
 * `401 {"message":"Invalid login credentials","exc_type":"AuthenticationError"}`.
 *
 * `apiBase()` is NOT consulted: this mode is same-origin by definition, and a base pointing
 * elsewhere would mean a cross-site cookie, which is the shape `A3d` was written about.
 */
export const LOGIN_METHOD = '/api/method/login'

export interface LoginBody {
  usr: string
  pwd: string
}

/** Frappe's own field names. The user is trimmed; the password never is — a space may be in it. */
export function loginBody(user: string, password: string): LoginBody {
  return { usr: user.trim(), pwd: password }
}

/**
 * What to tell the person. Frappe's own message is used wherever it has one — "Invalid login
 * credentials" says it better than we would, and a restriction or a lockout arrives the same way.
 * `status: 0` means the request never landed.
 */
export function explainLoginFailure(status: number, body: { message?: unknown } | null): string {
  if (status === 0) return 'Could not reach the server. Check your connection and try again.'
  const message = typeof body?.message === 'string' ? body.message.trim() : ''
  if (message !== '') return message
  return `The server could not sign you in (${status}). Try again, or tell whoever runs the site.`
}

export type LoginResult = { ok: true; fullName: string } | { ok: false; reason: string }

type LoginResponseBody = { message?: unknown; full_name?: unknown } | null

type Fetcher = (url: string, init: RequestInit) => Promise<{ status: number; json: () => Promise<unknown> }>

export async function signInWithPassword(user: string, password: string, fetcher: Fetcher = fetch as unknown as Fetcher): Promise<LoginResult> {
  let status = 0
  let body: LoginResponseBody = null
  try {
    const response = await fetcher(LOGIN_METHOD, {
      method: 'POST',
      // The cookie jar is the whole point: the response's `sid` is what authorises everything after.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(loginBody(user, password)),
    })
    status = response.status
    body = (await response.json().catch(() => null)) as LoginResponseBody
  } catch {
    return { ok: false, reason: explainLoginFailure(0, null) }
  }
  if (status < 200 || status >= 300) return { ok: false, reason: explainLoginFailure(status, body) }
  return { ok: true, fullName: typeof body?.full_name === 'string' ? body.full_name : '' }
}

/**
 * Did the session actually stick?
 *
 * Frappe sets `sid` as `Secure; HttpOnly; SameSite=None` (measured 2026-09-24), so a browser that
 * refuses a Secure cookie over plain `http://` drops it. The login itself returns 200, nothing is
 * kept, and the gate — asking who is signed in — hears "nobody" and shows the login page again.
 * Bouncing back to a blank login form is the least informative failure this flow can produce, so
 * the screen asks the site directly before declaring success.
 *
 * Deployed this cannot happen: the app is served BY the Frappe site, over the same HTTPS origin
 * the cookie was written for.
 */
export const SESSION_NOT_KEPT =
  'Signed in, but your browser did not keep the session. The site marks its session cookie ' +
  '“Secure”, which some browsers drop over a plain http:// address — open the app at ' +
  'http://localhost (not an IP address), or use a browser that allows it.'

const CURRENT_USER_METHOD = '/api/method/quoflow.quoflow.quoflow.api.auth.get_current_user'

export async function sessionIsLive(fetcher: Fetcher = fetch as unknown as Fetcher): Promise<boolean> {
  try {
    const response = await fetcher(CURRENT_USER_METHOD, { credentials: 'include' })
    if (response.status < 200 || response.status >= 300) return false
    const body = (await response.json().catch(() => null)) as { message?: { email?: unknown } } | null
    return typeof body?.message?.email === 'string' && body.message.email.trim() !== ''
  } catch {
    return false
  }
}
